import type { ComponentType, FunctionComponent, ReactElement, ReactNode } from "react";

import type { ComponentIconProps, IconName } from "@similarweb/icons";
import type { EPrimaryItemType, ESecondaryItemType, INavItemBadgeType } from "@similarweb/ui-components";

import type { ValuesOf } from "app/helpers/types/iterables";
import type { EGranularities } from "app/pages/app-intelligence/constants";
import type { EPrimarySideBar3IdType } from "app/reducers/_reducers/PrimarySidebar3Reducer";
import type { devicesTypes } from "app/UtilitiesAndConstants/Constants/DevicesTypes";

export enum BADGE_ITEMS {
    NEW = "new",
    BETA = "beta",
    INTERNAL = "internal",
    DATA20 = "data 2.0",
    ADDON = "addon",
    NUMBER = "number",
    NONE = "none",
    PREMIUM = "premium",
    PREMIUM_PLUS = "premium +",
    STANDARD = "standard",
    COMING_SOON = "coming soon",
    DEV = "dev",
}

export enum SecondarySideBarType3 {
    //market analysis
    IndustryResearch = "IndustryResearch",

    IndustryAnalysis = "IndustryAnalysis",
    SearchInterest = "SearchInterest",
    AppMarket = "AppMarket",
    //competitive
    WebsiteAnalysis = "WebsiteAnalysis",
    SegmentAnalysis = "SegmentAnalysis",
    AppAnalysis = "AppAnalysis",
    // sales intelligence
    SICompanyOverview = "SICompanyOverview",
    SIAppReview = "SIAppReview",
    //search intelligence
    Search = "Search",
    SearchTracking = "SearchTracking",
    KeywordResearch = "KeywordResearch",
    BacklinksAnalysis = "BacklinksAnalysis",
    SiteAudit = "SiteAudit",
    LLMTracking = "LLMTracking",
    LLMTraffic = "LLMTraffic",
    AIResearch = "AIResearch",
    Recommendations = "Recommendations",
    MonitorAndProtect = "MonitorAndProtect",
    //adintelligence
    AdvertiserActivity = "AdvertiserActivity",
    PublisherAnalysis = "PublisherAnalysis",
    AdTracker = "AdTracker",
    //sales intelligence — ad intelligence (SI copies)
    SalesAdvertiserActivity = "SalesAdvertiserActivity",
    SalesPublisherAnalysis = "SalesPublisherAnalysis",
    SalesMonitorAndProtect = "SalesMonitorAndProtect",
    //standAlone
    Dashboards = "Dashboards",
    ConversionAnalysis = "ConversionAnalysis",
    PremiumFeatures = "PremiumFeatures",
    BenchmarkTracker = "BenchmarkTracker",
    Activation = "Activation",
    None = "None",
}

interface INavItemOptions {
    isUSStatesSupported?: boolean;
    isVirtualSupported?: boolean;
}

export interface IOverrideParams {
    duration?: string;
    country?: number;
    timeGranularity?: EGranularities;
    webSource?: ValuesOf<typeof devicesTypes>;
}

interface IDefaultQueryParams {
    comparedDuration?: string;
    state?: string;
    webSource?: string;
    isWWW?: string;
    sort?: string | null;
    domain?: string | null;
}

export interface INavItem<T = Record<string, string>> {
    title: string;
    name: string;
    subItems?: INavItem[];
    state?: string;
    isBeta?: boolean | ((IStateParamsService: FixmeType) => boolean);
    isNew?: boolean;
    BadgeType?: INavItemBadgeType;
    options?: INavItemOptions;
    hidden?: boolean;
    // lock item/subitem
    lockIcon?: boolean;
    isLoading?: boolean; // dashboard only
    overrideParams?: IOverrideParams;
    defaultQueryParams?: IDefaultQueryParams;
    itemComponent?: FunctionComponent;
    itemType?: ESecondaryItemType;
    itemComponentProps?: T;
    tab?: any;
    id?: string;
    /**
     * for cases in Acquisition Channels, for example when we go to the SEO subSection
     * we don't want the "SEO Competitor Analysis" group will be selected even we are actually display the "SEO Competitor Analysis"
     * homepage
     * */
    disableSelectedMode?: boolean;
    isLocked?: boolean;
    onClickLocked?: VoidFunction;
    upsellBadgeComponent?: ReactElement | null;
}

export enum ESidebarPlacement {
    Top = "Top",
    Middle = "Middle",
    Bottom = "Bottom",
}

export enum EGroupTypes {
    GroupPrimaryLevel = "GroupPrimaryLevel",
    GroupSecondaryLevel = "GroupSecondaryLevel",
}

export interface IBadgeData {
    badgeType: INavItemBadgeType;
    badgeText?: string;
}

type combinePrimaryTypes = EGroupTypes | EPrimaryItemType;

interface IDefaultPrimarySidebarItemProps {
    title?: string;
    icon?: IconName | ComponentType<ComponentIconProps>;
    id?: EPrimarySideBar3IdType;
    isLocked?: boolean;
    onClickLocked?: VoidFunction;
    isHidden?: boolean;
    badgeData?: IBadgeData;
    onClick?: (
        state: string,
        id: string,
        itemCounter: number,
        isLocked: boolean,
        badgeType?: INavItemBadgeType,
        ParentTrackName?: string,
    ) => void;
    state?: any;
    trackName?: string;
}

export interface ISubSectionItemBase extends IDefaultPrimarySidebarItemProps {
    itemType: EPrimaryItemType;
    secondaryBarType3?: SecondarySideBarType3;
    ParentTrackName?: string;
    upsellBadgeComponent?: ReactElement;
}

export interface ISubSectionItem extends ISubSectionItemBase {
    children?: ISubSectionItemBase[];
}

export interface IPrimarySidebarItem extends IDefaultPrimarySidebarItemProps {
    itemType: combinePrimaryTypes;
    link?: string;
    subLinks?: string[];
    groupedSubSection?: ISubSectionItem[];
    secondaryBarType3?: SecondarySideBarType3;
    isLocked?: boolean;
    sidebarItemComponent?: (isBarOpen: boolean, onClick?: VoidFunction, onClose?: VoidFunction) => ReactNode;

    //Handle the placement of the item on the sidebar (menu / secondary items- like settings)
    placement: ESidebarPlacement;
    //Handle the placement of the items within the menu(placement attribute)
    menuPlacement?: ESidebarPlacement;
    upsellBadgeComponent?: ReactElement;
}

export type PrimarySidebarItems = Array<IPrimarySidebarItem>;

export const ADDON_BADGE_TEXTS = [BADGE_ITEMS.STANDARD, BADGE_ITEMS.PREMIUM, BADGE_ITEMS.PREMIUM_PLUS];
